import { usePageTitle } from "../hooks";

/**
 * PrivacyPolicyPage - Privacy Policy for JumpBean
 *
 * COPPA-compliant privacy policy for an educational app targeting preschool children.
 * Accessible to both authenticated and unauthenticated users.
 */
export function PrivacyPolicyPage() {
  usePageTitle("Privacy Policy");

  const lastUpdated = "January 5, 2026";

  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-b from-magic-100 via-sunshine-50 to-white">
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-neutral-900 mb-4 text-center">
            Privacy Policy
          </h1>
          <p className="font-body text-body-lg text-neutral-600 text-center">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <p className="font-body text-body-lg text-neutral-700 leading-relaxed">
                JumpBean ("we," "our," or "us") is committed to protecting the privacy of children
                and families who use our educational app. This Privacy Policy explains how we
                collect, use, and protect information when you use JumpBean.
              </p>
              <p className="font-body text-body-lg text-neutral-700 leading-relaxed mt-4">
                JumpBean is designed for preschool children (ages 4-5) and complies with the
                Children's Online Privacy Protection Act (COPPA). We take children's privacy
                seriously and have designed our app with privacy protection as a core principle.
              </p>
            </div>

            {/* Section 1 */}
            <PolicySection title="1. Information We Collect">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Parent/Guardian Information
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                When a parent or guardian creates an account, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700 mb-6">
                <li>Email address (for account login and communication)</li>
                <li>Password (securely hashed, never stored in plain text)</li>
                <li>Account preferences and settings</li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Child Profile Information
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                For child profiles, we collect only the minimum information necessary:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700 mb-6">
                <li>Display name (first name or nickname only—no last names required)</li>
                <li>Avatar selection (a choice from our pre-designed characters)</li>
              </ul>
              <p className="font-body text-body-md text-neutral-700">
                <strong>We do not collect:</strong> Last names, addresses, phone numbers, photos,
                or any other personally identifiable information from children.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Learning Progress Data
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                To provide educational value and help parents track progress, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Challenges completed and stars earned</li>
                <li>Time spent on activities</li>
                <li>Badges and achievements earned</li>
                <li>Learning streaks and play sessions</li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Subscription Information
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                If you subscribe to a paid plan, payment processing is handled entirely by Stripe,
                a PCI-compliant payment processor. We store only a reference to your Stripe
                customer account—we never store credit card numbers, CVVs, or full payment
                details on our servers.
              </p>
            </PolicySection>

            {/* Section 2 */}
            <PolicySection title="2. How We Use Information">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Provide and improve our educational content and features</li>
                <li>Track and display learning progress to parents</li>
                <li>Manage your account and subscription</li>
                <li>Send important account-related communications</li>
                <li>Ensure the security and integrity of our service</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                <strong>We never:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Sell personal information to third parties</li>
                <li>Use children's information for behavioral advertising</li>
                <li>Share children's information with advertisers</li>
                <li>Create public profiles for children</li>
              </ul>
            </PolicySection>

            {/* Section 3 */}
            <PolicySection title="3. Third-Party Services">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We use the following third-party services to operate JumpBean:
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Supabase (Database & Authentication)
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We use Supabase to securely store account information and learning progress. Data
                is encrypted in transit and at rest. Supabase is SOC 2 Type II certified.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Stripe (Payment Processing)
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                Stripe handles all payment processing for subscriptions. Stripe is PCI DSS Level 1
                certified, the highest level of payment security certification. We never see or
                store your full payment card details.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                PostHog (Analytics)
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We use PostHog for product analytics to understand how our app is used and improve
                the learning experience. Our analytics implementation is designed to be
                COPPA-compliant:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Only parent accounts are individually identified</li>
                <li>Child profiles are tracked using anonymous, randomized identifiers</li>
                <li>We do not collect or associate personal information with child activity</li>
                <li>No behavioral advertising or cross-site tracking</li>
              </ul>
            </PolicySection>

            {/* Section 4 */}
            <PolicySection title="4. Children's Privacy (COPPA Compliance)">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                JumpBean is designed with children's privacy as a priority. We comply with COPPA
                by:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  <strong>Parental Consent:</strong> Only parents can create accounts and child
                  profiles. Children cannot create accounts or share personal information.
                </li>
                <li>
                  <strong>Minimal Data Collection:</strong> We collect only a display name and
                  avatar choice for children—no photos, addresses, or detailed personal
                  information.
                </li>
                <li>
                  <strong>No Child-Directed Advertising:</strong> JumpBean contains no
                  advertisements of any kind. Children never see ads or promotional content.
                </li>
                <li>
                  <strong>No Social Features:</strong> There are no chat features, friend lists,
                  or ways for children to communicate with others through our app.
                </li>
                <li>
                  <strong>No External Links in Child UI:</strong> The child-facing portions of our
                  app do not contain links to external websites.
                </li>
                <li>
                  <strong>Parent Controls:</strong> Parents have full visibility into their
                  child's progress and can delete child profiles at any time.
                </li>
              </ul>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                For more details about our COPPA compliance, please see our{" "}
                <a href="/coppa" className="text-magic-600 hover:text-magic-700 underline">
                  COPPA Compliance page
                </a>
                .
              </p>
            </PolicySection>

            {/* Section 5 */}
            <PolicySection title="5. Data Security">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We implement appropriate technical and organizational measures to protect personal
                information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Encryption of data in transit using HTTPS/TLS</li>
                <li>Encryption of data at rest in our database</li>
                <li>Secure password hashing (passwords are never stored in plain text)</li>
                <li>Row-level security policies ensuring users can only access their own data</li>
                <li>Regular security reviews and updates</li>
                <li>Limited access to personal data by our team members</li>
              </ul>
            </PolicySection>

            {/* Section 6 */}
            <PolicySection title="6. Data Retention">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We retain personal information only as long as necessary to provide our services:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  <strong>Account Data:</strong> Retained while your account is active. Deleted
                  upon account deletion request.
                </li>
                <li>
                  <strong>Child Profile Data:</strong> Retained while the child profile exists.
                  When you delete a child profile, all associated progress data is permanently
                  deleted.
                </li>
                <li>
                  <strong>Subscription Data:</strong> Retained as required by Stripe and
                  applicable financial regulations.
                </li>
              </ul>
            </PolicySection>

            {/* Section 7 */}
            <PolicySection title="7. Your Rights">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                As a parent or guardian, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  <strong>Access:</strong> View your child's personal information and learning
                  progress through the parent dashboard.
                </li>
                <li>
                  <strong>Delete:</strong> Delete your child's profile at any time, which
                  permanently removes all associated data.
                </li>
                <li>
                  <strong>Refuse Collection:</strong> You can choose not to create a child profile
                  or provide certain information.
                </li>
                <li>
                  <strong>Account Deletion:</strong> Request complete deletion of your account and
                  all associated data.
                </li>
                <li>
                  <strong>Opt-Out of Analytics:</strong> Contact us to opt out of analytics
                  tracking.
                </li>
              </ul>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                To exercise any of these rights, please contact us using the information below.
              </p>
            </PolicySection>

            {/* Section 8 */}
            <PolicySection title="8. Changes to This Policy">
              <p className="font-body text-body-md text-neutral-700">
                We may update this Privacy Policy from time to time. If we make material changes
                to how we collect, use, or share children's personal information, we will notify
                parents by email and/or by posting a prominent notice in the app before the
                changes take effect. We encourage you to review this Privacy Policy periodically.
              </p>
            </PolicySection>

            {/* Section 9 */}
            <PolicySection title="9. Contact Us">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                If you have any questions about this Privacy Policy, our data practices, or would
                like to exercise your rights, please contact us:
              </p>
              <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-200">
                <p className="font-body text-body-md text-neutral-700">
                  <strong>JumpBean Privacy Team</strong>
                  <br />
                  Email:{" "}
                  <a
                    href="mailto:privacy@jumpbean.app"
                    className="text-magic-600 hover:text-magic-700 underline"
                  >
                    privacy@jumpbean.app
                  </a>
                </p>
              </div>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                We will respond to all legitimate requests within 30 days.
              </p>
            </PolicySection>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Reusable section component for policy content */
function PolicySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10 pb-10 border-b border-neutral-200 last:border-b-0">
      <h2 className="font-display font-bold text-display-sm text-neutral-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default PrivacyPolicyPage;
